import {FC} from 'react';
import countries from 'i18n-iso-countries';
import BlankCard from 'components/BlankCard/BlankCard';
import TabView from 'components/TabView/TabView';
import VacationParamsSection from './VacationParamsSection';
import HealthcareParamsTable from 'viewComponents/HealthcareParamsTable/HealthcareParamsTable';
import HolidaysParamsTable from 'viewComponents/HolidaysParamsTable/HolidaysParamsTable';
import MonetizationParamsTable from 'viewComponents/MonetizationParamsTable/MonetizationParamsTable';
import {useTranslation} from 'react-i18next';
import {ParamsConfigurationProps} from 'models/configuration';
import {Button, Chip} from '@mui/material';

interface CountryConfigurationProps extends ParamsConfigurationProps {
  /** Whether the country is active; when false the disable/enable buttons flip. */
  isActive?: boolean;
  /** Callback to open the disable-country modal for this country. */
  onDisable: () => void;
  /** Callback to open the enable-country modal for this country. */
  onEnable: () => void;
}

/**
 * Displays the parameter tabs for a single country inside the management module.
 *
 * @param props - Country configuration payload plus the refresh callback from the parent query
 * @returns The tabbed country configuration card.
 */
const CountryConfiguration: FC<CountryConfigurationProps> = ({
  country,
  inabilities,
  vacationRegulations,
  holidays,
  monetizationRules,
  fetch,
  isActive,
  onDisable,
  onEnable,
}) => {
  // Normalize: treat undefined as active so the default behaviour is permissive.
  const countryIsActive = isActive !== false;
  const {t, i18n} = useTranslation();

  const items = [
    {
      label: t('leaves.CountryConfiguration.holidays'),
      view: (
        <HolidaysParamsTable
          fetch={fetch}
          country={country}
          values={holidays}
          isActive={countryIsActive}
        />
      ),
    },
    {
      label: t('leaves.CountryConfiguration.healthcare'),
      view: (
        <HealthcareParamsTable
          fetch={fetch}
          country={country}
          values={inabilities}
          isActive={countryIsActive}
        />
      ),
    },
    {
      label: t('leaves.CountryConfiguration.vacationDays'),
      view: (
        <VacationParamsSection
          country={country}
          vacationRegulations={vacationRegulations}
          fetch={fetch}
          isActive={countryIsActive}
        />
      ),
    },
  ];

  if (monetizationRules !== null && monetizationRules !== undefined) {
    items.push({
      label: t('leaves.CountryConfiguration.monetization'),
      view: (
        <MonetizationParamsTable
          fetch={fetch}
          country={country}
          isActive={countryIsActive}
        />
      ),
    });
  }

  return (
    <div className={`mt-4${isActive === false ? ' opacity-75' : ''}`}>
      <BlankCard
        title={
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span>
                {t('leaves.CountryConfiguration.title', {
                  country: countries.getName(country, i18n.language),
                })}
              </span>
              {isActive === false && (
                <Chip
                  label={t('leaves.CountryConfiguration.inactiveLabel')}
                  size="small"
                  sx={{color: 'grey.500', borderColor: 'grey.400'}}
                  variant="outlined"
                />
              )}
            </div>
            <div>
              {isActive !== false ? (
                <Button variant="outlined" size="small" onClick={onDisable}>
                  {t('leaves.CountryConfiguration.disableButton')}
                </Button>
              ) : (
                <Button variant="outlined" size="small" onClick={onEnable}>
                  {t('leaves.CountryConfiguration.enableButton')}
                </Button>
              )}
            </div>
          </div>
        }
      >
        <TabView tabs={items} />
      </BlankCard>
    </div>
  );
};

export default CountryConfiguration;
